import { FunctionComponent, ReactElement, ReactNode } from "react";
import { SmartLink } from "@/src/components/v2/SmartLink/SmartLink";
import clsx from "clsx";

export interface ButtonProps {
    style?: "Primary" | "Secondary" | "Link" | "Surface" | "outline-secondary" | "outline-primary";
    title: string | ReactNode;
    href?: string;
    target?: string;
    rel?: string;
    addArrow?: boolean;
    inline?: boolean;
    className?: string;
    disabled?: boolean;
    type?: "button" | "submit" | "reset";
    loading?: boolean;
    onClick?: () => void;
    id?: string;
    linkWrapperClass?: string;
    textWrapperClass?: string;
    /** Classes on the arrow wrapper (default "pr-4"); lets callers match a different button anatomy. */
    arrowWrapperClass?: string;
    buttonAnimation?: ButtonAnimation;
}

export type ButtonAnimation = "heartbeat" | "shimmer" | "" | undefined;

export const Button: FunctionComponent<ButtonProps> = ({
    style,
    title,
    href,
    target,
    rel,
    addArrow,
    inline,
    className,
    disabled,
    loading = false,
    type = "button",
    onClick,
    id,
    linkWrapperClass,
    textWrapperClass,
    arrowWrapperClass = "pr-4",
    buttonAnimation
}) => {
    function renderArrow(): ReactNode {
        return (
            <span className={arrowWrapperClass}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M11.2927 5.45964C11.6833 5.06912 12.3164 5.06912 12.7069 5.45964L18.5403 11.293C18.7278 11.4805 18.8332 11.7349 18.8332 12.0001C18.8332 12.2653 18.7278 12.5197 18.5403 12.7072L12.7069 18.5405C12.3164 18.931 11.6833 18.931 11.2927 18.5405C10.9022 18.15 10.9022 17.5168 11.2927 17.1263L15.419 13.0001H6.1665C5.61422 13.0001 5.1665 12.5524 5.1665 12.0001C5.1665 11.4478 5.61422 11.0001 6.1665 11.0001H15.419L11.2927 6.87385C10.9022 6.48333 10.9022 5.85017 11.2927 5.45964Z"
                        fill="currentColor"
                    />
                </svg>
            </span>
        );
    }

    // 1. Define the Inner Shimmer Effect (Shiny bar inside the button)
    const InnerShimmer = buttonAnimation === "shimmer" && (
        <div
            className="pointer-events-none absolute left-0 top-1/2 z-20 h-[200%] w-[57px] animate-shimmer-button-loop"
            style={{
                background: "linear-gradient(270deg, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.75) 50%, rgba(255, 255, 255, 0.00) 100%)",
                transform: "translateY(-50%) rotate(15deg)",
                mixBlendMode: "color-dodge"
            }}
        />
    );

    // 2. The Visual Button (The span that looks like a button)
    // We added 'relative z-10' to stack it above rings, and 'overflow-hidden' ONLY for shimmer.
    const buttonContent = (
        <span
            className={clsx(
                `button w-full ${style !== "Link" && "shadow-xs"} md:w-auto ${style ?? "Primary"} ${style ?? "Surface" ? "bg-background-surface" : ""} 
            ${style ?? "outline-secondary"} cursor-pointer
            ${inline ? "inline-flex" : "flex md:inline-flex"} items-center justify-center py-3 ${className ?? "font-semibold"}`,
                "relative z-10",
                buttonAnimation === "shimmer" && "overflow-hidden"
            )}
        >
            {InnerShimmer}
            <span className={`flex-1 px-10 text-center ${addArrow ? "pl-16 pr-6" : loading ? "pl-10 pr-3" : ""} ${textWrapperClass ?? ""}`}>{title}</span>
            {loading ? <Spinner /> : addArrow ? renderArrow() : null}
        </span>
    );

    // 3. Logic to wrap the content with external animations (Heartbeat Rings or Faux Border)
    let finalContent = buttonContent;

    const pulseClass = "bg-accent-primary absolute rounded-full";

    if (buttonAnimation === "heartbeat") {
        finalContent = (
            <div className="group relative inline-flex w-full justify-center md:w-auto">
                <div className={clsx("z-30 animate-heartbeat-sm", pulseClass)} />
                <div className={clsx("z-20 animate-heartbeat-md", pulseClass)} />
                <div className={clsx("z-10 animate-heartbeat-lg", pulseClass)} />
                <div className="relative z-40">{buttonContent}</div>
            </div>
        );
    } else if (buttonAnimation === "shimmer") {
        finalContent = (
            <div className="group relative inline-flex w-full justify-center md:w-auto">
                {/* Faux Border */}
                <div className={clsx("absolute -inset-[2px] z-0 overflow-hidden rounded-full bg-[#A8280A]")}>
                    <div
                        className="pointer-events-none absolute top-1/2 h-[200%] w-[57px] animate-shimmer-border-loop"
                        style={{
                            background: "linear-gradient(270deg, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.85) 50%, rgba(255, 255, 255, 0.00) 100%)",
                            transform: "translateY(-50%) rotate(-15deg)"
                        }}
                    />
                </div>
                {buttonContent}
            </div>
        );
    }

    // 4. Return the correct container (Link vs Button)
    if (href) {
        return (
            <SmartLink href={href} target={target} rel={rel} className={clsx("button-link", linkWrapperClass)}>
                {finalContent}
            </SmartLink>
        );
    }

    return (
        <button type={type} onClick={onClick} className={`button ${className ?? ""}`} disabled={disabled} id={id}>
            {finalContent}
        </button>
    );
};

function Spinner(): ReactElement {
    return (
        <svg className="text-white -ml-1 mr-3 h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
        </svg>
    );
}
